import { useState } from 'react';
import { ContractVersion } from '../../types/contracts';
import { formatDistanceToNow, format } from 'date-fns';
import { Clock, Eye } from 'lucide-react';
import { Modal } from '../Modal';
import { ContractViewer } from './ContractViewer';

interface VersionHistoryProps {
  versions: ContractVersion[];
  isCreator: boolean;
}

export function VersionHistory({ versions, isCreator }: VersionHistoryProps) {
  const [selectedVersion, setSelectedVersion] = useState<ContractVersion | null>(null);

  // Sort versions newest first
  const sortedVersions = [...versions].sort((a, b) => b.version_number - a.version_number);

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-[var(--t-text)] flex items-center gap-2">
        <Clock size={18} className="text-[var(--t-text-muted)]" />
        Version History
      </h3>

      <div className="bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl overflow-hidden">
        {sortedVersions.map((version, idx) => (
          <div
            key={version.id}
            className={`p-4 flex items-center justify-between ${idx !== sortedVersions.length - 1 ? 'border-b border-[var(--t-border)]' : ''} hover:bg-[var(--t-surface-hover)] transition-colors`}
          >
            <div className="flex flex-col">
              <span className="font-medium text-[var(--t-text)]">
                Version {version.version_number} {idx === 0 ? <span className="ml-2 text-xs bg-[var(--t-primary-dim)] text-[var(--t-primary)] px-2 py-0.5 rounded-full font-bold">CURRENT</span> : ''}
              </span>
              <span className="text-xs text-[var(--t-text-muted)] mt-1">
                {format(new Date(version.created_at), 'MMM d, yyyy h:mm a')} ({formatDistanceToNow(new Date(version.created_at), { addSuffix: true })})
              </span>
              {version.change_summary && (
                <span className="text-sm mt-2 text-[var(--t-text-secondary)] italic">
                  "{version.change_summary}"
                </span>
              )}
            </div>

            <button
              onClick={() => setSelectedVersion(version)}
              className="p-2 text-[var(--t-text-muted)] hover:text-[var(--t-primary)] hover:bg-[var(--t-primary-dim)] rounded-lg transition-colors"
              title="View Version"
            >
              <Eye size={18} />
            </button>
          </div>
        ))}

        {versions.length === 0 && (
          <div className="p-6 text-center text-[var(--t-text-muted)]">
            No versions saved yet.
          </div>
        )}
      </div>

      <Modal
        isOpen={!!selectedVersion}
        onClose={() => setSelectedVersion(null)}
        title={`Version ${selectedVersion?.version_number} Preview`}
        maxWidth="max-w-5xl"
      >
        <div className="p-6 bg-[var(--t-bg)]">
          {selectedVersion && (
            <ContractViewer
              content={selectedVersion.content}
              viewerRole={isCreator ? 'agent' : 'buyer'}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
